import {
  useEffect,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';

import type { PendingFocusChip } from '../../chip-group/contexts/InternalChipGroupContext.js';

/**
 * Moves focus onto the first revealed chip after "Show more" expands the group.
 * Cleanup happens on blur.
 * @internal
 */
export function usePendingFocusAnnouncement(
  chipRef: RefObject<HTMLElement | null>,
  options: {
    isPendingFocus: boolean;
    isVisible: boolean;
    disabled: boolean;
    uniqueId: string;
    setPendingFocusChip?: Dispatch<SetStateAction<PendingFocusChip>>;
  },
): void {
  const { isPendingFocus, isVisible, disabled, uniqueId, setPendingFocusChip } =
    options;

  useEffect(() => {
    if (!isPendingFocus || !isVisible) {
      return;
    }
    const chipWrapper = chipRef.current;
    if (!chipWrapper) {
      return;
    }

    const removeFocusAttributes = (): void => {
      chipWrapper.removeAttribute('tabindex');
      chipWrapper.removeAttribute('aria-label');
      chipWrapper.removeAttribute('aria-disabled');
      chipWrapper.removeAttribute('role');
    };

    const handleBlur = (): void => {
      removeFocusAttributes();
      setPendingFocusChip?.((current) =>
        current?.uniqueId === uniqueId ? null : current,
      );
    };

    // Apply a temporary tabindex of -1 to allow programmatic focus onto
    // the non-interactive wrapper when it is the first revealed chip.
    chipWrapper.setAttribute('tabindex', '-1');
    chipWrapper.setAttribute('role', 'group');
    chipWrapper.setAttribute('aria-disabled', String(disabled));
    // Use the chip surface's text content as the accessible name so the screen
    // reader announces the chip when focus lands on the non-interactive wrapper.
    const chipLabel = chipWrapper
      .querySelector('.strato-surface')
      ?.textContent?.trim();
    if (chipLabel) {
      chipWrapper.setAttribute('aria-label', chipLabel);
    }

    chipWrapper.focus();
    chipWrapper.addEventListener('blur', handleBlur, { once: true });

    return () => {
      removeFocusAttributes();
      chipWrapper.removeEventListener('blur', handleBlur);
      setPendingFocusChip?.((current) =>
        current?.uniqueId === uniqueId ? null : current,
      );
    };
  }, [
    chipRef,
    disabled,
    isPendingFocus,
    isVisible,
    setPendingFocusChip,
    uniqueId,
  ]);
}
